import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import {
  LayoutDashboard,
  MapPin,
  Users,
  Plane,
  ShoppingCart,
  UserCircle,
  Mail,
  Inbox,
  Sparkles,
  Zap,
  Image,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Clock,
  Check,
  Shield,
  FileText,
  GitBranch,
  BarChart3,
} from "lucide-react";
import clsx from "clsx";

const navigation = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard },
  { name: "Analytics", href: "/analytics", icon: BarChart3 },
  { name: "Destinace", href: "/destinations", icon: MapPin },
  { name: "Průvodci", href: "/guides", icon: Users },
  { name: "Zájezdy", href: "/trips", icon: Plane },
  { name: "Objednávky", href: "/orders", icon: ShoppingCart },
  { name: "Zákazníci", href: "/customers", icon: UserCircle },
  { name: "Recenze", href: "/reviews", icon: MessageSquare },
  { name: "Zprávy", href: "/messages", icon: Inbox },
  { name: "Newsletter", href: "/newsletter", icon: Mail },
  { name: "AI Segmenty", href: "/segments", icon: Sparkles },
  { name: "Automatizace", href: "/automations", icon: Zap },
  { name: "Workflow", href: "/workflows", icon: GitBranch },
  { name: "Média", href: "/media", icon: Image },
  { name: "Šablony", href: "/templates", icon: FileText },
  { name: "Uživatelé", href: "/users", icon: Shield },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handlePublishInfo = () => {
    setPublishSuccess(true);
    setTimeout(() => setPublishSuccess(false), 5000);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-64 transform bg-sidebar transition-transform duration-200 ease-in-out lg:static lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-white.svg" alt="CK Go2" className="h-8 w-auto" />
            <span className="rounded bg-accent-500 px-1.5 py-0.5 text-xs font-medium text-white">
              Admin
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-gray-400 hover:text-white lg:hidden"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3">
          {navigation.map((item) => {
            const isActive =
              item.href === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.href);

            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={clsx(
                  "mb-1 flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary-600 text-white"
                    : "text-gray-300 hover:bg-white/10 hover:text-white"
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-700 p-4">
          <Link
            to="/settings"
            className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Settings className="h-5 w-5" />
            Nastavení
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top header */}
        <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800 lg:px-6">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white lg:hidden"
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Breadcrumb / Title */}
          <div className="hidden lg:block">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {navigation.find(
                (n) =>
                  n.href === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(n.href)
              )?.name || "Admin"}
            </h1>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Cache info button */}
            <button
              onClick={handlePublishInfo}
              className={clsx(
                "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                publishSuccess
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              )}
            >
              {publishSuccess ? (
                <>
                  <Check className="h-4 w-4" />
                  <span className="hidden sm:inline">Změny se projeví do 5 min</span>
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4" />
                  <span className="hidden sm:inline">Auto-publikace: 5 min</span>
                </>
              )}
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300">
                  {user?.name.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:block">{user?.name}</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 z-50 mt-2 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <div className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user?.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user?.email}
                      </p>
                      <p className="mt-1 text-xs text-primary-600 dark:text-primary-400">
                        {user?.role}
                      </p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <LogOut className="h-4 w-4" />
                      Odhlásit se
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
